/**
 * Typed WebSocket client for an Ancient Nine online room.
 *
 * Wraps the protocol in src/worker/protocol.ts: connects to /ws/:code with a
 * stable per-browser pid (so a refresh reclaims the same seat), auto-joins,
 * reconnects with jittered exponential backoff (and at once when the tab
 * comes back to the foreground or the network returns), queues messages sent
 * while reconnecting, sends heartbeats, and exposes the latest room snapshot.
 *
 *   const room = new RoomClient({ code: 'ABCD', name: 'Blair' });
 *   const off = room.subscribe((snap) => render(snap));
 *   room.connect();
 *   room.addBot('medium');
 */
import {
  CLOSE_BAD_REQUEST,
  CLOSE_ROOM_EXPIRED,
  CLOSE_ROOM_NOT_FOUND,
  PID_HEADER,
  PING_FRAME,
  cleanName,
  isRoomCode,
} from '../../worker/protocol';
import type {
  BotLevel,
  ClientMessage,
  CreateRoomResponse,
  Emote,
  RoomInfoResponse,
  RoomView,
  ServerMessage,
} from '../../worker/protocol';
import type { Action, GameState } from '../../engine';
import { migrateLegacyKeys } from '../lib/storage';

export type ConnectionStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

/** Why the client stopped for good (no more reconnects). */
export type FatalReason = 'room-not-found' | 'room-expired' | 'bad-request' | 'closed-by-client';

export interface RoomSnapshot {
  /** Engine state redacted for this viewer; null while the room is in its lobby. */
  state: GameState | null;
  /** Your seat index, or null while spectating. */
  you: number | null;
  /** Host's seat index, or null if no human holds a seat. */
  host: number | null;
  room: RoomView;
}

export interface RoomClientEvents {
  state: (snap: RoomSnapshot) => void;
  status: (status: ConnectionStatus, fatal?: FatalReason) => void;
  error: (message: string) => void;
  emote: (seat: number, emote: Emote) => void;
  /** Tibbs's readout: status, play, a further chunk, done (worker/protocol.ts). */
  readout: (msg: ReadoutMessage) => void;
}

/** The room's readout messages, as received (docs/TIBBS-VOICE.md). */
export type ReadoutMessage = Extract<ServerMessage, { t: 'readoutStatus' | 'readout' | 'readoutChunk' | 'readoutDone' }>;

export interface RoomClientOptions {
  code: string;
  name: string;
  /** Override the stored player id (tests, multiple tabs as different players). */
  pid?: string;
  /** Origin to connect to; defaults to the page origin. */
  origin?: string;
  /** Inject a WebSocket implementation (tests). */
  WebSocketImpl?: typeof WebSocket;
}

const PID_STORAGE_KEY = 'ancientnine.pid';
const HEARTBEAT_MS = 25_000;
const BACKOFF_BASE_MS = 500;
const BACKOFF_MAX_MS = 10_000;
/** Messages held while reconnecting (emotes are never queued). */
const MAX_QUEUE = 20;

// ---------------------------------------------------------------------------
// Player id + REST helpers
// ---------------------------------------------------------------------------

function randomPid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** The browser's stable player id, created on first use and kept in localStorage. */
export function getPlayerId(): string {
  migrateLegacyKeys(); // carries a pre-rename niners.pid over, so a returning player keeps their seat
  try {
    const existing = localStorage.getItem(PID_STORAGE_KEY);
    if (existing && /^[A-Za-z0-9_-]{8,64}$/.test(existing)) return existing;
    const pid = randomPid();
    localStorage.setItem(PID_STORAGE_KEY, pid);
    return pid;
  } catch {
    return randomPid(); // storage blocked: this tab gets its own id
  }
}

/** Create a new room. Resolves to its 4-letter code. */
export async function createRoom(origin = location.origin): Promise<string> {
  const res = await fetch(`${origin}/api/rooms`, { method: 'POST' });
  if (!res.ok) throw new Error(`Could not create a room (${res.status}).`);
  const body = (await res.json()) as CreateRoomResponse;
  return body.code;
}

/**
 * Look up a room by code. Resolves to null if it does not exist (or has
 * expired). `seated` says whether this browser's pid still holds a seat there.
 */
export async function getRoomInfo(
  code: string,
  { origin = location.origin, pid = getPlayerId() }: { origin?: string; pid?: string } = {},
): Promise<RoomInfoResponse | null> {
  const normalized = normalizeRoomCode(code);
  if (!normalized) return null;
  const res = await fetch(`${origin}/api/rooms/${normalized}`, { headers: { [PID_HEADER]: pid } });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Could not look up room (${res.status}).`);
  return (await res.json()) as RoomInfoResponse;
}

/** Uppercase and validate a user-typed room code; null if it cannot be one. */
export function normalizeRoomCode(input: string): string | null {
  const code = input.trim().toUpperCase();
  return isRoomCode(code) ? code : null;
}

// ---------------------------------------------------------------------------
// RoomClient
// ---------------------------------------------------------------------------

type Listeners = { [K in keyof RoomClientEvents]: Set<RoomClientEvents[K]> };

export class RoomClient {
  readonly code: string;
  readonly pid: string;
  private name: string;
  private readonly origin: string;
  private readonly WS: typeof WebSocket;

  private ws: WebSocket | null = null;
  private _status: ConnectionStatus = 'idle';
  private _snapshot: RoomSnapshot | null = null;
  private attempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private stopped = false;
  private queue: ClientMessage[] = [];
  private readonly wake = () => this.reconnectNow();
  private readonly listeners: Listeners = {
    state: new Set(),
    status: new Set(),
    error: new Set(),
    emote: new Set(),
    readout: new Set(),
  };

  constructor(opts: RoomClientOptions) {
    const code = normalizeRoomCode(opts.code);
    if (!code) throw new Error(`Not a room code: ${opts.code}`);
    this.code = code;
    this.name = cleanName(opts.name) ?? 'Golfer';
    this.pid = opts.pid ?? getPlayerId();
    this.origin = opts.origin ?? location.origin;
    this.WS = opts.WebSocketImpl ?? WebSocket;
  }

  get status(): ConnectionStatus {
    return this._status;
  }

  /** Latest snapshot from the server, or null before the first one arrives. */
  get snapshot(): RoomSnapshot | null {
    return this._snapshot;
  }

  /** Is this client the room's host? */
  get isHost(): boolean {
    const s = this._snapshot;
    return !!s && s.you !== null && s.you === s.host;
  }

  // -- subscriptions --------------------------------------------------------

  on<K extends keyof RoomClientEvents>(event: K, fn: RoomClientEvents[K]): () => void {
    (this.listeners[event] as Set<RoomClientEvents[K]>).add(fn);
    return () => (this.listeners[event] as Set<RoomClientEvents[K]>).delete(fn);
  }

  /** Subscribe to room snapshots. Fires immediately if one is already known. */
  subscribe(fn: (snap: RoomSnapshot) => void): () => void {
    const off = this.on('state', fn);
    if (this._snapshot) fn(this._snapshot);
    return off;
  }

  private emit<K extends keyof RoomClientEvents>(event: K, ...args: Parameters<RoomClientEvents[K]>): void {
    for (const fn of this.listeners[event] as Set<(...a: Parameters<RoomClientEvents[K]>) => void>) {
      try {
        fn(...args);
      } catch (err) {
        console.error(`RoomClient ${event} listener threw`, err);
      }
    }
  }

  // -- lifecycle ------------------------------------------------------------

  connect(): void {
    this.stopped = false;
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.wake);
      document.addEventListener('visibilitychange', this.wake);
    }
    if (this.ws && this.ws.readyState <= 1) return;
    this.open();
  }

  /** Close for good. No further reconnects. */
  close(): void {
    this.stopped = true;
    this.queue = [];
    this.clearTimers();
    this.removeWakeListeners();
    this.ws?.close(1000, 'bye');
    this.ws = null;
    this.setStatus('closed', 'closed-by-client');
  }

  private open(): void {
    this.setStatus(this.attempts === 0 ? 'connecting' : 'reconnecting');
    const url = new URL(`/ws/${this.code}`, this.origin);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.searchParams.set('pid', this.pid);
    url.searchParams.set('name', this.name);

    const ws = new this.WS(url.toString());
    this.ws = ws;
    ws.onopen = () => {
      if (ws !== this.ws) return;
      this.attempts = 0;
      this.setStatus('open');
      this.raw({ t: 'join', name: this.name });
      for (const m of this.queue.splice(0)) this.raw(m);
      this.startHeartbeat();
    };
    ws.onmessage = (ev: MessageEvent) => {
      if (ws !== this.ws || typeof ev.data !== 'string') return;
      this.receive(ev.data);
    };
    ws.onclose = (ev: CloseEvent) => {
      if (ws !== this.ws) return;
      this.ws = null;
      this.clearTimers();
      const fatal = fatalFor(ev.code);
      if (fatal) {
        this.stopped = true;
        this.queue = [];
        this.removeWakeListeners();
        this.setStatus('closed', fatal);
      } else if (!this.stopped) {
        this.scheduleReconnect();
      }
    };
    ws.onerror = () => {
      // onclose follows and decides what to do.
    };
  }

  private scheduleReconnect(): void {
    const delay = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** this.attempts);
    const jittered = delay / 2 + Math.random() * (delay / 2);
    this.attempts += 1;
    this.setStatus('reconnecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.stopped) this.open();
    }, jittered);
  }

  /** Skip the backoff wait when the tab is visible again or the network is back. */
  private reconnectNow(): void {
    if (this.stopped || this._status !== 'reconnecting' || !this.reconnectTimer) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.open();
  }

  private removeWakeListeners(): void {
    if (typeof window === 'undefined') return;
    window.removeEventListener('online', this.wake);
    document.removeEventListener('visibilitychange', this.wake);
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === 1) this.ws.send(PING_FRAME);
    }, HEARTBEAT_MS);
  }

  private clearTimers(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.reconnectTimer = null;
    this.heartbeatTimer = null;
  }

  private setStatus(status: ConnectionStatus, fatal?: FatalReason): void {
    if (status === this._status && !fatal) return;
    this._status = status;
    this.emit('status', status, fatal);
  }

  private receive(data: string): void {
    let msg: ServerMessage;
    try {
      msg = JSON.parse(data) as ServerMessage;
    } catch {
      return;
    }
    switch (msg.t) {
      case 'state':
        this._snapshot = { state: msg.state, you: msg.you, host: msg.host, room: msg.room };
        this.emit('state', this._snapshot);
        break;
      case 'error':
        this.emit('error', msg.message);
        break;
      case 'emote':
        this.emit('emote', msg.seat, msg.emote);
        break;
      case 'readoutStatus':
      case 'readout':
      case 'readoutChunk':
      case 'readoutDone':
        this.emit('readout', msg);
        break;
      case 'pong':
        break;
    }
  }

  // -- sending --------------------------------------------------------------

  /** Send a raw protocol message now. Returns false if the socket is not open. */
  private raw(msg: ClientMessage): boolean {
    if (this.ws?.readyState !== 1) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  /**
   * Send now, or hold it until the socket reopens (after the automatic join).
   * Returns true if sent immediately.
   */
  private sendOrQueue(msg: ClientMessage): boolean {
    if (this.raw(msg)) return true;
    if (!this.stopped && this.queue.length < MAX_QUEUE) this.queue.push(msg);
    return false;
  }

  /** Take (or rename) a seat. Sent automatically on every (re)connect. */
  join(name: string): boolean {
    this.name = cleanName(name) ?? this.name;
    return this.raw({ t: 'join', name: this.name });
  }

  /** Host: seat a computer player. */
  addBot(level: BotLevel): boolean {
    return this.sendOrQueue({ t: 'addBot', level });
  }

  /** Host, lobby only: free a seat. */
  removeSeat(seat: number): boolean {
    return this.sendOrQueue({ t: 'removeSeat', seat });
  }

  /** Host: deal a new game (also a rematch after game over). */
  start(holes: number): boolean {
    return this.sendOrQueue({ t: 'start', holes });
  }

  /** Play a move. The server checks it against the engine's legal actions. */
  act(action: Action): boolean {
    return this.sendOrQueue({ t: 'action', action });
  }

  /** Between holes: say you are ready. The next hole starts when every connected player is. */
  ready(): boolean {
    return this.act({ type: 'nextHole' } as Action);
  }

  /** Host, between holes: start the next hole without waiting. */
  forceNext(): boolean {
    return this.sendOrQueue({ t: 'forceNext' });
  }

  /** Host: end the game and return everyone to the lobby. */
  backToLobby(): boolean {
    return this.sendOrQueue({ t: 'backToLobby' });
  }

  /** Leave the table (lobby: seat freed; mid-game: a bot plays your seat). Stays connected as a spectator. */
  leave(): boolean {
    return this.sendOrQueue({ t: 'leave' });
  }

  /** Preset emotes only. Never queued: a stale emote is worse than none. */
  emote(emote: Emote): boolean {
    return this.raw({ t: 'chat', emote });
  }

  /** Ask Tibbs to speak (`h<n>` or `final`). Never queued: a late readout is worse than none. */
  tibbs(key: string): boolean {
    return this.raw({ t: 'tibbs', key });
  }
}

function fatalFor(code: number): FatalReason | null {
  if (code === CLOSE_ROOM_NOT_FOUND) return 'room-not-found';
  if (code === CLOSE_ROOM_EXPIRED) return 'room-expired';
  if (code === CLOSE_BAD_REQUEST) return 'bad-request';
  return null;
}
